import {useFormik} from "formik";
import {Box, Button, Grid, TextField, Typography} from "@mui/material";
import {useSnackbar} from "notistack";
import FormTextInputField from "../input/FormTextField";
import {useTranslation} from "react-i18next";
import {CancelRequest} from "../../app/api/types";
import DefaultDialog, {DefaultDialogSize} from "./ClosableDialog";
import React from "react";

export interface CancelFormProps<T> {
    entity?: T;
    title: string;
    bodyText?: string;
    titleCancel: string;
    isOpen: boolean;
    onSubmit: (requestData: CancelRequest) => Promise<void>;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function CancelDialog<T extends {id: number}>({
    entity,
    title,
    bodyText,
    isOpen,
    titleCancel,
    onSubmit,
    onSuccess,
    onClose,
}: CancelFormProps<T>) {
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const form = useFormik({
        initialValues: {
            id: entity?.id,
            cancelReason: "",
        },
        onSubmit: async (values) => {
            const requestData = {
                id: values?.id,
                cancelReason: values?.cancelReason,
            } as CancelRequest;

            await onSubmit(requestData);
            enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});

            onSuccess?.();
        },
    });

    const handleSubmit = () => form.handleSubmit();

    return (
        <DefaultDialog
            size={DefaultDialogSize.LARGE}
            open={isOpen}
            titleKey={t(title)}
            onClose={onClose}
            actions={[
                <Box key="cancel">
                    <Button color="primary" variant="contained" type="submit" onClick={handleSubmit}>
                        {titleCancel}
                    </Button>
                </Box>,
            ]}
        >
            <form>
                <Grid container sx={{marginTop: "12px"}} xs={12}>
                    <Grid item>
                        <Typography variant={"body1"}>{bodyText}</Typography>
                    </Grid>
                    <Grid item sx={{marginTop: "12px"}} xs={12}>
                        <TextField type={"hidden"} id={"id"} name={"id"} value={entity?.id} sx={{display: "none"}} />
                        <FormTextInputField
                            label={t("generic_cancelReason")}
                            fieldName={"cancelReason"}
                            form={form}
                            multiline
                            minRows={10}
                            isGridItem={true}
                            gridProps={{xs: 12}}
                        />
                    </Grid>
                </Grid>
            </form>
        </DefaultDialog>
    );
}
